import HomeIcon from "@material-ui/icons/Home";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import { useFormikContext } from "formik";
import React, { Fragment, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import useFormatters from "../../../../../../components/FormattedFields/useFormatters";
import {
  FormikCheckbox,
  FormikRadioGroup,
  FormikTextField,
} from "../../../../../../components/FormikFields";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import {
  SERVICE_HOLDER_HOME_TYPE_FLAT,
  SERVICE_HOLDER_HOME_TYPE_HOUSE,
  SERVICE_HOLDER_HOME_FEATURE_ELEVATOR,
  SERVICE_HOLDER_HOME_FEATURE_ADAPTED_BATHROOM,
  SERVICE_HOLDER_HOME_FEATURE_CRANE,
  SERVICE_HOLDER_HOME_FEATURE_CAMERAS,
  SERVICE_HOLDER_HOME_FEATURE_ARTICULATED_BED,
  SERVICE_HOLDER_HOME_FEATURE_PETS,
  SERVICE_HOLDER_HOME_FEATURE_CLEAN_SERVICE,
  SERVICE_HOLDER_HOME_FEATURE_TELE_ASSISTANCE,
  SERVICE_HOLDER_COMPANION_ALONE,
  SERVICE_HOLDER_COMPANION_WITH_PARTNER,
  SERVICE_HOLDER_COMPANION_WITH_RELATIVE,
} from "../../../../../../util/defaults/serviceHolder";
import FormAccordion from "../../../ServicePage/components/FormComponents/FormAccordion";
import FormBox from "../../../ServicePage/components/FormComponents/FormBox";
import FormBoxTitle from "../../../ServicePage/components/FormComponents/FormBoxTitle";
import AddressForm from "../../../ServicePage/components/ServiceValuation/components/FormSections/AddressForm";
import ServiceHoldersMiniForm from "../../../ServicePage/components/ServiceValuation/components/FormSections/ServiceHolderMiniForm";
import ServiceHoldersForm from "../../../ServicePage/components/ServiceValuation/components/FormSections/ServiceHoldersForm";
import {
  TCrossSellingOption,
  TServiceFollowUpForm,
  TServiceFollowUpFormError,
} from "../../models/ServiceFollowUpForm";
import SimpleAutocomplete from "../../../../../../components/FormikFields/SimpleAutocomplete";
import { TContactInfo } from "../../../ServicePage/components/ServiceValuation/types";
import ContactsTable from "../../../ServicePage/components/ServiceValuation/components/ContactsTable";
import { serviceIsSelection } from "../../../../models/Service/Service";
import SelectionFollowUpForm from "./SelectionFollowUpForm";
import ValidationErrorsDialog from "../../../../components/ValidationErrorsDialog";
import { TGetServicesCollectionItem } from "../../../../../../api/service/service.types";
import UsersToTakeCareRadioGroup from "../../../ServiceEditValuationPage/UsersToTakeCareRadioGroup/UsersToTakeCareRadioGroup";

const StyledContainer = styled(Box)`
  padding: 1rem 0;
`;

export const StyledField = styled.div`
  width: 100%;
  padding-top: 0;
  padding-bottom: 0;

  &:last-child {
    border-bottom: none;
  }

  .CMuiCoordinatorFileTask {
    .MuiFormControlLabel-label {
      line-height: 1;
    }
  }
  .CMuiCoordinatorFilePathology {
    .MuiFormControlLabel-label {
      line-height: 1;
    }
  }
`;

export const StyledLabel = styled(Typography)`
  color: ${cuideoTheme.palette.primary.light};
  font-size: 0.8rem;
  white-space: wrap;
  padding: 0.2rem 0 0.2rem 0;

  @media all and (min-width: ${cuideoTheme.breakpoints.values.md}px) {
    font-size: 0.8rem;
  }
`;

const StyledContent = styled.div`
  padding-bottom: 8rem;
`;

const StyledActions = styled.div`
  position: absolute;
  background-color: #e3ebee;
  padding: 0 0.75rem 1rem 1rem;
  border-radius: 0.5rem 0 0 0;
  box-shadow: 0 0 4px 2px #e3ebee;
  bottom: 0;
  right: 0.5rem;
  width: 14rem;
`;

const StyledActionButtonsWrapper = styled(Box)`
  .MuiButton-root {
    margin: 0.5rem 0 0 0.75rem;
  }
`;

const StyledButton = styled(Button)``;

interface IServiceFollowUpForm {
  service: TGetServicesCollectionItem;
  pathologyOptions: { value: string; label: string }[];
  areaOptions: { value: string; label: string }[];
  regionOptions: { value: string; label: string }[];
  familiarUnitOptions: { value: string; label: string }[];
  speechOptions: { value: string; label: string }[];
  mobilityOptions: { value: string; label: string }[];
  emotionalStatusOptions: { value: string; label: string }[];
  careAdaptationOptions: { value: string; label: string }[];
  sleepQualityOptions: { value: string; label: string }[];
  toiletLevelOptions: { value: string; label: string }[];
  salaryTypeOptions: { value: string; label: string }[];
  officialDegreeOptions: { value: string; label: string }[];
  serviceHolderUserStatusOptions: { value: string; label: string }[];
  dependencyOptions: { value: string; label: string }[];
}

const ServiceFollowUpForm = (props: IServiceFollowUpForm) => {
  const {
    service,
    pathologyOptions,
    areaOptions,
    regionOptions,
    speechOptions,
    mobilityOptions,
    emotionalStatusOptions,
    careAdaptationOptions,
    sleepQualityOptions,
    toiletLevelOptions,
    serviceHolderUserStatusOptions,
    dependencyOptions,
  } = props;
  const formikContext = useFormikContext<TServiceFollowUpForm>();
  const { formatMessage } = useIntl();
  const {
    formatServiceHolderHomeType,
    formatServiceHolderHomeFeature,
    formatFamiliarUnit,
  } = useFormatters();
  const [errors, setErrors] = useState<TServiceFollowUpFormError[]>([]);

  const handleUnitSelect = (newValue: string | null) => {
    formikContext.setFieldValue("Unidad_de_convivencia", newValue);
  };

  const handleUnitChange = (newValue: string | null) => {
    formikContext.setFieldValue("Unidad_de_convivencia", newValue);
  };

  const handleUnitBlur = () => {
    formikContext.setFieldTouched("Unidad_de_convivencia", true);
  };

  const handleContactsChange = (newContacts: TContactInfo[]) => {
    formikContext.setFieldValue("Contactos", newContacts);
  };

  const handleAcceptValidationDialog = () => {
    setErrors([]);
  };

  const handleSubmitClick = async (e: React.MouseEvent) => {
    const newErrors: TServiceFollowUpFormError[] = [];
    await formikContext.validateForm();
    const entries = Object.entries(formikContext.errors);
    entries.forEach(([fieldName, message]) => {
      if (typeof message === "string") {
        newErrors.push({
          type: "validation_error",
          field: fieldName,
          message,
        });
      }
    });
    setErrors(newErrors);
  };

  const handleHandFootCareQuestionChange = (newValue: TCrossSellingOption) => {
    if (newValue === formikContext.values.Pregunta_podologia) {
      formikContext.setFieldValue("Pregunta_podologia", null);
    } else {
      formikContext.setFieldValue("Pregunta_podologia", newValue);
    }
  };

  const handleMedicCareQuestionChange = (newValue: TCrossSellingOption) => {
    if (newValue === formikContext.values.Pregunta_consejos_medicos) {
      formikContext.setFieldValue("Pregunta_consejos_medicos", null);
    } else {
      formikContext.setFieldValue("Pregunta_consejos_medicos", newValue);
    }
  };

  const handleHealthyFoodQuestionChange = (newValue: TCrossSellingOption) => {
    if (newValue === formikContext.values.Pregunta_comida_saludable) {
      formikContext.setFieldValue("Pregunta_comida_saludable", null);
    } else {
      formikContext.setFieldValue("Pregunta_comida_saludable", newValue);
    }
  };

  const handleStimulationQuestionChange = (newValue: TCrossSellingOption) => {
    if (newValue === formikContext.values.Pregunta_estimulacion) {
      formikContext.setFieldValue("Pregunta_estimulacion", null);
    } else {
      formikContext.setFieldValue("Pregunta_estimulacion", newValue);
    }
  };

  return (
    <StyledContainer>
      <StyledContent>
        <FormAccordion
          summary={
            <FormattedMessage
              id="ServiceValuationForm.Segumiento del Servicio"
              defaultMessage="Seguimiento del Servicio"
            />
          }
          details={
            <Fragment>
              <FormBox>
                <FormBoxTitle
                  title={formatMessage({
                    id: "ServiceValuationForm.FamiliarUnitInfo",
                    defaultMessage: "Información Unidad Familiar",
                  })}
                  icon={<HomeIcon />}
                />
                <Grid container spacing={8}>
                  <Grid item xs={12} md={6}>
                    <UsersToTakeCareRadioGroup />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SimpleAutocomplete
                      onSelect={handleUnitSelect}
                      onInputChange={handleUnitChange}
                      onBlur={handleUnitBlur}
                      hasError={!!formikContext.errors.Unidad_de_convivencia}
                      touched={formikContext.touched.Unidad_de_convivencia}
                      errorMessage={
                        formikContext.errors.Unidad_de_convivencia ?? undefined
                      }
                      inputValue={formikContext.values.Unidad_de_convivencia}
                      value={formikContext.values.Unidad_de_convivencia}
                      name="Unidad_de_convivencia"
                      options={[
                        formatFamiliarUnit(SERVICE_HOLDER_COMPANION_ALONE),
                        formatFamiliarUnit(
                          SERVICE_HOLDER_COMPANION_WITH_PARTNER
                        ),
                        formatFamiliarUnit(
                          SERVICE_HOLDER_COMPANION_WITH_RELATIVE
                        ),
                      ]}
                      label={
                        <FormattedMessage
                          id="ServiceValuationForm.FamilyUnit"
                          defaultMessage="¿Con cuántas personas convive el usuario?"
                        />
                      }
                    />
                  </Grid>
                </Grid>
                <AddressForm
                  service={service}
                  areaOptions={areaOptions}
                  regionOptions={regionOptions}
                />
              </FormBox>
              {service.Tipo_de_Servicio === "Cuideo Asist" ? (
                <ServiceHoldersMiniForm
                  pathologyOptions={pathologyOptions}
                  mobilityOptions={mobilityOptions}
                />
              ) : (
                <ServiceHoldersForm
                  pathologyOptions={pathologyOptions}
                  speechOptions={speechOptions}
                  mobilityOptions={mobilityOptions}
                  emotionalStatusOptions={emotionalStatusOptions}
                  careAdaptationOptions={careAdaptationOptions}
                  sleepQualityOptions={sleepQualityOptions}
                  toiletLevelOptions={toiletLevelOptions}
                  serviceHolderUserStatusOptions={
                    serviceHolderUserStatusOptions
                  }
                  dependencyOptions={dependencyOptions}
                />
              )}
              {service.Tipo_de_Servicio !== "Cuideo Asist" && (
                <FormBox>
                  <FormBoxTitle
                    title={formatMessage({
                      id: "ServiceValuationForm.HomeFeatures",
                      defaultMessage: "Características de la vivienda familiar",
                    })}
                    icon={<HomeIcon />}
                  />
                  <Grid container spacing={8}>
                    <Grid item xs={12} md={6}>
                      <FormikRadioGroup
                        name="Tipo_de_vivienda"
                        fullWidth
                        label={
                          <FormattedMessage
                            id="ServiceValuationForm.HomeType"
                            defaultMessage="Tipo de vivienda"
                          />
                        }
                        size="small"
                        variant="compact"
                        required
                        options={[
                          {
                            label: formatServiceHolderHomeType(
                              SERVICE_HOLDER_HOME_TYPE_FLAT
                            ),
                            value: SERVICE_HOLDER_HOME_TYPE_FLAT,
                          },
                          {
                            label: formatServiceHolderHomeType(
                              SERVICE_HOLDER_HOME_TYPE_HOUSE
                            ),
                            value: SERVICE_HOLDER_HOME_TYPE_HOUSE,
                          },
                        ]}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={8}>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_elevator"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_ELEVATOR
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_adapted_bathroom"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_ADAPTED_BATHROOM
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_crane"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_CRANE
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_cameras"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_CAMERAS
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_articulated_bed"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_ARTICULATED_BED
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_pets"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_PETS
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_clean_service"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_CLEAN_SERVICE
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <FormikCheckbox
                        name="home_tele_asistance"
                        variant="compact"
                        label={formatServiceHolderHomeFeature(
                          SERVICE_HOLDER_HOME_FEATURE_TELE_ASSISTANCE
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      {formikContext.values.home_tele_asistance && (
                        <FormikTextField
                          name="Proveedor_de_Teleasistencia"
                          label={
                            <FormattedMessage
                              id="ServiceValuationForm.TeleasistanceProvider"
                              defaultMessage="Proveedor de Teleasistencia"
                            />
                          }
                          fullWidth
                          margin="nomargin"
                        />
                      )}
                    </Grid>
                    <Grid item xs={6} md={8}></Grid>
                  </Grid>
                </FormBox>
              )}
            </Fragment>
          }
        />

        <FormAccordion
          summary={
            <FormattedMessage
              id="ServiceValuationForm.ContactInformation"
              defaultMessage="Información de Contacto"
            />
          }
          details={
            <Fragment>
              <ContactsTable
                contacts={formikContext.values.Contactos}
                onContactsChange={handleContactsChange}
              />
            </Fragment>
          }
        />

        {serviceIsSelection(service) && (
          <FormAccordion
            summary={
              <FormattedMessage
                id="ServiceFollowUp.Segumiento comercial"
                defaultMessage="Seguimiento comercial"
              />
            }
            details={
              <SelectionFollowUpForm
                handFootCareQuestion={formikContext.values.Pregunta_podologia}
                onHandFootCareQuestionChange={handleHandFootCareQuestionChange}
                medicCareQuestion={
                  formikContext.values.Pregunta_consejos_medicos
                }
                onMedicCareQuestionChange={handleMedicCareQuestionChange}
                healthyFoodQuestion={
                  formikContext.values.Pregunta_comida_saludable
                }
                onHealthyFoodQuestionChange={handleHealthyFoodQuestionChange}
                stimulationQuestion={formikContext.values.Pregunta_estimulacion}
                onStimulationQuestionChange={handleStimulationQuestionChange}
              />
            }
          />
        )}
      </StyledContent>
      <StyledActions>
        <StyledActionButtonsWrapper>
          <StyledButton
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSubmitClick}
          >
            <FormattedMessage
              id="ServiceValutaionForm.Guardar seguimiento"
              defaultMessage="Guardar seguimiento"
            />
          </StyledButton>
        </StyledActionButtonsWrapper>
      </StyledActions>
      <ValidationErrorsDialog
        open={errors.length > 0}
        errors={errors}
        onAccept={handleAcceptValidationDialog}
      />
    </StyledContainer>
  );
};

export default ServiceFollowUpForm;
